import { useEffect, useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  type CatalogApi,
} from '@backstage/plugin-catalog-react';

type Resolver = (entity: Entity | undefined) => void;

interface Loader {
  cache: Map<string, Entity | undefined>;
  pending: Map<string, Resolver[]>;
  scheduled: boolean;
}

const loaders = new WeakMap<CatalogApi, Loader>();

function getLoader(catalogApi: CatalogApi): Loader {
  let loader = loaders.get(catalogApi);
  if (!loader) {
    loader = { cache: new Map(), pending: new Map(), scheduled: false };
    loaders.set(catalogApi, loader);
  }
  return loader;
}

async function flush(catalogApi: CatalogApi, loader: Loader): Promise<void> {
  const batch = loader.pending;
  loader.pending = new Map();
  loader.scheduled = false;

  const entityRefs = [...batch.keys()];
  let items: (Entity | undefined)[] = [];
  try {
    ({ items } = await catalogApi.getEntitiesByRefs({ entityRefs }));
  } catch {
    batch.forEach(resolvers =>
      resolvers.forEach(resolve => resolve(undefined)),
    );
    return;
  }

  entityRefs.forEach((ref, index) => {
    const entity = items[index];
    loader.cache.set(ref, entity);
    batch.get(ref)?.forEach(resolve => resolve(entity));
  });
}

function load(
  catalogApi: CatalogApi,
  ref: string,
): Promise<Entity | undefined> {
  const loader = getLoader(catalogApi);

  if (loader.cache.has(ref)) {
    return Promise.resolve(loader.cache.get(ref));
  }

  return new Promise(resolve => {
    const resolvers = loader.pending.get(ref);
    if (resolvers) {
      resolvers.push(resolve);
    } else {
      loader.pending.set(ref, [resolve]);
    }

    if (!loader.scheduled) {
      loader.scheduled = true;

      setTimeout(() => flush(catalogApi, loader), 0);
    }
  });
}

export function useEntityByRef(ref: string | undefined): Entity | undefined {
  const catalogApi = useApi(catalogApiRef);
  const [entity, setEntity] = useState<Entity | undefined>(undefined);

  useEffect(() => {
    if (!ref) {
      setEntity(undefined);
      return undefined;
    }

    let cancelled = false;
    load(catalogApi, ref).then(result => {
      if (!cancelled) {
        setEntity(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [catalogApi, ref]);

  return entity;
}
